import { useNavigate, useSearch } from '@tanstack/react-router';

import type {
  AppRequestBooleanFilter,
  AppRequestDatePreset,
  AppRequestSortProperty,
  SupplyAppsSortDirection,
} from './model';
import { defaultAppRequestsListFilter } from './model';
import {
  parseAppRequestsRouteSearch,
  serializeAppRequestsRouteSearch,
} from './appRequestsRouteSearch';

export function useAppRequestsRouteFilters(publisherScoped = false) {
  const navigate = useNavigate({
    from: publisherScoped
      ? '/supply/publishers/$publisherId/details/app-requests'
      : '/supply/apps/requests',
  });
  const search = useSearch({ strict: false });
  const filter = parseAppRequestsRouteSearch(search);
  const update = (next: typeof filter) => {
    void navigate({ search: serializeAppRequestsRouteSearch(next), replace: true });
  };
  return {
    filter,
    setSearch: (searchTerm: string) => {
      update({ ...filter, searchTerm, page: 0 });
    },
    setPublishers: (publisherIds: string[]) => {
      update({ ...filter, publisherIds, page: 0 });
    },
    setPlatforms: (platformIds: string[]) => {
      update({ ...filter, platformIds, page: 0 });
    },
    setProcessed: (processed: AppRequestBooleanFilter) => {
      update({ ...filter, processed, page: 0 });
    },
    setBlocked: (blocked: AppRequestBooleanFilter) => {
      update({ ...filter, blocked, page: 0 });
    },
    setConnected: (connectedWithApp: AppRequestBooleanFilter) => {
      update({ ...filter, connectedWithApp, page: 0 });
    },
    setBundleIds: (bundleIds: string) => {
      update({ ...filter, bundleIds, page: 0 });
    },
    setAppStoreUrls: (appStoreUrls: string) => {
      update({ ...filter, appStoreUrls, page: 0 });
    },
    resetSecondaryFilters: () => {
      update({
        ...filter,
        publisherIds: defaultAppRequestsListFilter.publisherIds,
        platformIds: defaultAppRequestsListFilter.platformIds,
        processed: defaultAppRequestsListFilter.processed,
        blocked: defaultAppRequestsListFilter.blocked,
        connectedWithApp: defaultAppRequestsListFilter.connectedWithApp,
        bundleIds: defaultAppRequestsListFilter.bundleIds,
        appStoreUrls: defaultAppRequestsListFilter.appStoreUrls,
        page: 0,
      });
    },
    setDatePreset: (datePreset: AppRequestDatePreset) => {
      update({ ...filter, datePreset, page: 0 });
    },
    setSort: (
      sortProperty: AppRequestSortProperty | null,
      sortDirection: SupplyAppsSortDirection,
    ) => {
      update({ ...filter, sortProperty, sortDirection, page: 0 });
    },
    setPagination: (page: number, size: number) => {
      update({ ...filter, page: size === filter.size ? page : 0, size });
    },
  };
}
